import { isEmpty } from 'lodash';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { checkoutStage, upgradeReferralClick } from '@wix/bi-logger-pricing-plans-data/v2';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { CheckoutPublicData } from '@wix/pricing-plans-router-utils';
import type { CreateControllerFn, ControllerParams } from '@wix/yoshi-flow-editor';
import { demoOrderFixture, demoPlanFixture } from '../../fixtures';
import { createBenefitsApi, createOrdersApi, createPlansApi, createPremiumApi } from '../../services';
import { Analytics } from '../../services/analytics';
import { commonBIDataFromFlowAPI } from '../../services/commonBIData';
import { createPaymentMethodsApi } from '../../services/payment-methods';
import { createSettingsApi } from '../../services/settings';
import { WarmupData } from '../../services/WarmupData';
import { CheckoutStage, noModal, ToastData } from '../../types/common';
import { resolveLocale } from '../../utils';
import { TB8465_createOnLoginHandlerWithRetry } from '../../utils/createOnLoginHandlerWithTB8465Workaround';
import { isMultiPageApp } from '../../utils/isMultiPageApp';
import { getUserData } from '../../utils/user';
import { CheckoutController } from '../PackagePicker/controller/CheckoutController';
import { Navigation } from '../PackagePicker/controller/Navigation';
import { Router } from '../PackagePicker/controller/Router';
import { CheckoutWidgetProps } from '../PackagePicker/Widget/Checkout';

const createController: CreateControllerFn = async ({ flowAPI }: ControllerParams) => {
  const { setProps: untypedSetProps, wixCodeApi, appParams, compId, config } = flowAPI.controllerConfig;

  const noop = () => {};
  const setProps: (props: Partial<CheckoutWidgetProps>) => void = untypedSetProps;

  const plansApi = createPlansApi(flowAPI);
  const premiumApi = createPremiumApi(flowAPI);
  const ordersApi = createOrdersApi(flowAPI);
  const benefitsApi = createBenefitsApi(flowAPI);
  const settingsApi = createSettingsApi(flowAPI);
  const paymentMethodsApi = createPaymentMethodsApi(flowAPI, appParams.instanceId);
  const warmupData = new WarmupData(compId, wixCodeApi, flowAPI);
  const nav = new Navigation(flowAPI, appParams.appDefinitionId, await isMultiPageApp(wixCodeApi, appParams));
  const analytics = new Analytics(wixCodeApi.window);
  const router = new Router(setProps, nav, analytics, wixCodeApi, flowAPI);
  const checkout = new CheckoutController(
    setProps,
    wixCodeApi,
    router,
    flowAPI,
    plansApi,
    ordersApi,
    benefitsApi,
    premiumApi,
    paymentMethodsApi,
    analytics,
    settingsApi,
    warmupData,
  );

  const setupCheckoutDefaults = () => {
    flowAPI.bi?.updateDefaults(commonBIDataFromFlowAPI(flowAPI));
    wixCodeApi.seo.renderSEOTags({ itemType: 'PAYMENT_PAGE', itemData: {} });
    setProps({
      user: getUserData(wixCodeApi.user.currentUser),
      biCheckoutStage: noop,
      integrationData: {},
      appInstanceId: appParams.instanceId,
      siteOwnerId: flowAPI.controllerConfig.platformAPIs.bi?.ownerId,
      visitorId: flowAPI.controllerConfig.platformAPIs.bi?.visitorId,
      metaSiteId: flowAPI.controllerConfig.platformAPIs.bi?.metaSiteId,
      instance: appParams.instance,
      benefits: [],
      selectedPlan: {},
      modal: noModal,
      fitToContentHeight: true,
      hideToast: () => setProps({ toast: undefined }),
      showToast: (toast: ToastData) => setProps({ toast }),
      biUpgradeReferralClick: (referralInfo: string) =>
        flowAPI.bi?.report(upgradeReferralClick({ referralInfo, isPreview: false })),
      locale: resolveLocale(wixCodeApi),
      isCheckoutDataInitialized: false,
      purchaseLimitExceeded: false,
      couponInputMode: 'trigger',
      hasCoupons: false,
      onEditGuestEmail: noop,
      logout: noop,
      loginOnCheckout: noop,
      signupOnCheckout: noop,
      navigateToStatus: noop,
      trackInitiateCheckout: noop,
      trackSelectPayment: noop,
      demoBuyNowClicked: noop,
      biPlanPurchased: noop,
      updatePriceDetails: noop,
      removeCoupon: noop,
      onBeforeStartPayment: noop,
      onGetFreePlan: noop,
      onGetFullyDiscountedPlan: noop,
      onContinueAsGuest: noop,
    });
  };

  const loadCheckout = async () => {
    const data = wixCodeApi.window.getRouterPublicData<CheckoutPublicData>();
    flowAPI.bi?.report(
      checkoutStage({
        stage: CheckoutStage.CHECKOUT_LOAD_START,
        planGuid: data?.plan.id,
        guestCheckout: data?.checkoutData.guestCheckoutEnabled,
        referralInfo: data?.checkoutData.integrationData.biOptions?.referralInfo,
        isCustomCheckoutFormEnabled: Boolean(data?.plan.formId).toString(),
        isCustomStartDateEnabled: Boolean(data?.plan.allowFutureStartDate).toString(),
        checkoutFlowId: data?.checkoutData.checkoutFlowId,
      }),
    );
    if (data && !isEmpty(data)) {
      const { checkoutData, plan, order } = data;

      wixCodeApi.site.onInstanceChanged(({ instance: newInstance }) => {
        setProps({ instance: newInstance });
      }, appParams.appDefinitionId);

      TB8465_createOnLoginHandlerWithRetry(wixCodeApi, (user) => checkout.initializeUser?.(user));

      if (!plan) {
        await router.gotoList(checkoutData.integrationData, {
          message: flowAPI.translations.t('pp.checkout-missing-plan'),
        });
      } else {
        try {
          await checkout.update({
            selectedPlan: plan,
            integrationData: checkoutData.integrationData,
            order,
            guestCheckoutFlag: checkoutData.guestCheckoutEnabled,
            checkoutFlowId: data.checkoutData.checkoutFlowId,
          });
        } catch (e) {
          const { message } = flowAPI.errorHandler.getResolvedError(e);
          const isErrorHandlerEnabled = flowAPI.experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA);
          setProps({
            toast: {
              message: isErrorHandlerEnabled ? message : flowAPI.translations.t('pp.generic-purchase-error'),
            },
            isCheckoutDataInitialized: true,
          });
        }
      }
    } else {
      const [plan] = await loadPlansFallback();
      const { environment, translations } = flowAPI;
      if (environment.isViewer && plan === undefined) {
        await router.gotoList(
          {},
          {
            message: flowAPI.translations.t('pp.checkout-missing-plan'),
          },
        );
      } else {
        const selectedPlan = plan ?? config.publicData?.APP?.demoData?.plans?.[0] ?? demoPlanFixture(translations.t);
        const order = plan ? undefined : demoOrderFixture(translations.t);
        await checkout.update({
          selectedPlan,
          integrationData: {},
          order,
        });
      }
    }
  };

  const loadPlansFallback = async () => {
    const isErrorHandlerEnabled = flowAPI.experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA);
    let plans: PublicPlan[] = [];

    try {
      plans = await plansApi.query();
    } catch (e) {
      if (isErrorHandlerEnabled) {
        // mark error as resolved;
        flowAPI.errorHandler.getResolvedError(e);
      } else {
        throw e;
      }
    }
    return plans;
  };

  return {
    async pageReady() {
      setupCheckoutDefaults();

      if (!flowAPI.environment.isSSR) {
        loadCheckout();
      } else {
        await loadCheckout();
      }
    },
    async updateConfig(_$w, newConfig) {},
  };
};

export default createController;
